import React from "react";
import QrCode from "react-qr-code";
import type { IExpandedCallSheetData } from "../../../../types/callsheet";
import type { CrewEntry } from "./types/callsheet";
import logoBrown from "../../../../assets/logoBrown.png";
import type { CallSheetData } from "./types/callsheet";
import type { IThemeState } from "../../../../types/theme";
import { getCallSheetCompanyDetails } from "./callSheetCompanyDetails";

interface PrintableCallSheetProps {
  callSheetData: IExpandedCallSheetData;
  crewMembers: CrewEntry[];
  timeData: CallSheetData | null;
  reduxTheme: IThemeState;
  specialRequest: string;
}

const toBase64 = async (url: string): Promise<string> => {
  const res = await fetch(url);
  const blob = await res.blob();

  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.readAsDataURL(blob);
  });
};

const PrintableCallSheet = React.forwardRef<
  HTMLDivElement,
  PrintableCallSheetProps
>(
  (
    { callSheetData, crewMembers, timeData, reduxTheme, specialRequest },
    ref
  ) => {
    const companyDetails = getCallSheetCompanyDetails(callSheetData?.booking);

    const isExecutiveProducer = (role?: string) =>
      role?.toLowerCase() === "executive producer";

    const executiveProducerEntry = crewMembers?.find(
      (entry) =>
        entry.roles?.some((r) => isExecutiveProducer(r.name)) ||
        entry.assignment?.some((a) => isExecutiveProducer(a.role))
    );

    const isProducer = (role?: string) => role?.toLowerCase() === "producer";

    const producerEntry = crewMembers?.find(
      (entry) =>
        entry.roles?.some((r) => isProducer(r.name)) ||
        entry.assignment?.some((a) => isProducer(a.role))
    );

    const executiveProducer = executiveProducerEntry
      ? {
          name: `${executiveProducerEntry.crew_member.user.first_name} ${executiveProducerEntry.crew_member.user.last_name}`,
          email: executiveProducerEntry.crew_member.user.email,
        }
      : null;

    const producer = producerEntry
      ? {
          name: `${producerEntry.crew_member.user.first_name} ${producerEntry.crew_member.user.last_name}`,
          email: producerEntry.crew_member.user.email,
        }
      : null;

    const [deceasedImageBase64, setDeceasedImageBase64] = React.useState<
      string | null
    >(null);

    React.useEffect(() => {
      const imageUrl = callSheetData?.booking?.deceased?.[0]?.person_image;

      if (!imageUrl) return;

      toBase64(imageUrl)
        .then(setDeceasedImageBase64)
        .catch(() => setDeceasedImageBase64(null));
    }, [callSheetData]);
    const printStyles = {
      container: {
        width: "100%",
        maxWidth: "210mm",
        margin: "0 auto",
        padding: "20mm",
        fontFamily: "Arial, sans-serif",
        fontSize: "12px",
        lineHeight: 1.4,
        color: "#000",
        backgroundColor: "#fff",
      },
      headerGrid: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr",
        gap: "16px",
        marginBottom: "20px",
        borderBottom: "1px solid #ddd",
        paddingBottom: "16px",
      },
      leftColumn: {
        borderRight: "1px solid #ddd",
        paddingRight: "12px",
      },
      centerColumn: {
        textAlign: "center" as const,
        borderRight: "1px solid #ddd",
        padding: "0 12px",
      },
      rightColumn: {
        paddingLeft: "12px",
      },
      logo: {
        maxWidth: "120px",
        height: "auto",
        marginBottom: "8px",
      },
      avatar: {
        width: "60px",
        height: "60px",
        margin: "0 auto 8px auto",
        borderRadius: "50%",
      },
      name: {
        fontSize: "18px",
        fontWeight: "bold",
        marginBottom: "8px",
      },
      location: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: "12px",
        fontSize: "11px",
      },
      scheduleGrid: {
        display: "grid",
        gridTemplateColumns: "auto 1fr",
        gap: "4px 8px",
        marginTop: "8px",
      },
      scheduleLabel: {
        fontWeight: "bold",
        fontSize: "10px",
      },
      scheduleValue: {
        fontSize: "10px",
      },
      table: {
        width: "100%",
        borderCollapse: "collapse" as const,
        marginTop: "16px",
      },
      tableCell: {
        border: "1px solid #ddd",
        padding: "6px 8px",
        fontSize: "10px",
        textAlign: "left" as const,
      },
      tableHeader: {
        backgroundColor: reduxTheme.primary_colors[800],
        fontWeight: "bold",
      },
      clientDetails: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "16px",
        marginTop: "16px",
      },
      clientBox: {
        border: "1px solid #ddd",
        padding: "12px",
      },
      clientTitle: {
        fontSize: "12px",
        fontWeight: "bold",
        marginBottom: "8px",
        backgroundColor: reduxTheme.primary_colors[800],
        padding: "4px 8px",
        margin: "-12px -12px 8px -12px",
      },
      qrCode: {
        marginTop: "12px",
      },
      divider: {
        border: "none",
        borderTop: "1px solid #ddd",
        margin: "8px 0",
      },
    };

    const roleOrder = [
      "Executive Producer",
      "Producer",
      "Assistant Producer",
      "Liveswitcher",
      "Camera",
      "Networking",
      "Sound",
      "Tribute Table",
    ];

    const sortedCrewMembers = [...crewMembers].sort((a, b) => {
      const roleA = a.assignment?.[0]?.role || "N/A";
      const roleB = b.assignment?.[0]?.role || "N/A";

      const indexA = roleOrder.findIndex(
        (role) => role.toLowerCase() === roleA.toLowerCase()
      );
      const indexB = roleOrder.findIndex(
        (role) => role.toLowerCase() === roleB.toLowerCase()
      );

      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return 0;
    });
    return (
      <div ref={ref} style={printStyles.container}>
        {/* Header Grid */}
        <div style={printStyles.headerGrid}>
          {/* Left Column */}
          <div style={printStyles.leftColumn}>
            <img alt="Muzukuru logo" src={logoBrown} style={printStyles.logo} />
            <hr style={printStyles.divider} />

            <div style={{ marginTop: "16px" }}>
              <div
                style={{
                  fontWeight: "bold",
                  fontSize: "11px",
                  marginBottom: "4px",
                }}
              >
                {callSheetData?.booking?.company?.name || "N/A"}
              </div>
              <div style={{ fontSize: "10px", marginBottom: "8px" }}>
                {callSheetData?.booking?.company?.name || "N/A"}
              </div>
            </div>

            <hr style={printStyles.divider} />

            <div style={{ marginTop: "16px" }}>
              <div
                style={{
                  fontWeight: "bold",
                  fontSize: "10px",
                  marginBottom: "4px",
                }}
              >
                Executive Producer:
              </div>
              <div style={{ fontSize: "9px", marginBottom: "2px" }}>
                {executiveProducer?.name || "Joseph Bunga"}
              </div>
              <div style={{ fontSize: "9px", marginBottom: "8px" }}>
                {executiveProducer?.email || "joe@area46.co.zw"}
              </div>

              <div
                style={{
                  fontWeight: "bold",
                  fontSize: "10px",
                  marginBottom: "4px",
                }}
              >
                Producer:
              </div>
              <div style={{ fontSize: "9px", marginBottom: "2px" }}>
                {producer?.name || "N/A"}
              </div>
              <div style={{ fontSize: "9px" }}>{producer?.email || "N/A"}</div>
            </div>
          </div>

          {/* Center Column */}
          <div style={printStyles.centerColumn}>
            <div
              style={{
                ...printStyles.avatar,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "24px",
                fontWeight: "bold",
              }}
            >
              {deceasedImageBase64 ? (
                <img
                  src={deceasedImageBase64}
                  alt="Person"
                  style={{
                    width: "60px",
                    height: "60px",
                    borderRadius: "50%",
                    objectFit: "cover",
                  }}
                />
              ) : (
                callSheetData?.booking?.deceased[0]?.first_name
                  ?.charAt(0)
                  .toUpperCase() || "?"
              )}
            </div>

            <div style={printStyles.name}>
              {callSheetData?.booking?.deceased[0]?.first_name || ""}{" "}
              {callSheetData?.booking?.deceased[0]?.surname || ""}
            </div>

            <div style={printStyles.location}>
              <span style={{ marginRight: "4px" }}>📍</span>
              <span>
                {callSheetData?.booking?.services?.[0]?.venue?.name || ""},{" "}
                {callSheetData?.booking?.services?.[0]?.venue?.address_one || ""}
                {callSheetData?.booking?.services?.[0]?.venue?.address_two || ""}
              </span>
            </div>

            {callSheetData?.website_stream_link && (
              <div style={printStyles.qrCode}>
                <div style={{ fontSize: "9px", marginBottom: "4px" }}>
                  Scan Me
                </div>
                <QrCode size={50} value={callSheetData.website_stream_link} />
                <div
                  style={{
                    fontSize: "8px",
                    marginTop: "4px",
                    wordBreak: "break-all",
                  }}
                >
                  {callSheetData.website_stream_link}
                </div>
              </div>
            )}
          </div>

          {/* Right Column */}
          <div style={printStyles.rightColumn}>
            <div
              style={{
                fontSize: "12px",
                fontWeight: "bold",
                marginBottom: "8px",
              }}
            >
              {callSheetData?.date &&
                new Date(callSheetData.date).toLocaleDateString("en-GB", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
            </div>

            <hr style={printStyles.divider} />

            <div style={{ fontSize: "10px", margin: "8px 0" }}>
              <div style={{ fontWeight: "bold" }}>Harare</div>
              <div>overcast clouds</div>
              <div>10.64°C</div>
            </div>

            <hr style={printStyles.divider} />

            <div style={printStyles.scheduleGrid}>
              {[
                {
                  label: "Call Time:",
                  value: `${timeData?.call_time?.substring(
                    0,
                    5
                  )} - ${timeData?.departure_time?.substring(0, 5)}`,
                },
                {
                  label: "Travelling:",
                  value: `${timeData?.arrival_at_venue_time?.substring(
                    0,
                    5
                  )} - ${timeData?.setup_from_time?.substring(0, 5)}`,
                },
                {
                  label: "Setup:",
                  value: `${timeData?.setup_from_time?.substring(
                    0,
                    5
                  )} - ${timeData?.setup_to_time?.substring(0, 5)}`,
                },
                {
                  label: "Shoot:",
                  value: `${timeData?.shoot_start_time?.substring(
                    0,
                    5
                  )} - ${timeData?.shoot_end_time?.substring(0, 5)}`,
                },
                {
                  label: "Wrap:",
                  value: `${timeData?.estimated_wrap_time?.substring(
                    0,
                    5
                  )} - ${timeData?.back_to_base_time?.substring(0, 5)}`,
                },
              ].map((item) => (
                <React.Fragment key={item.label}>
                  <div style={printStyles.scheduleLabel}>{item.label}</div>
                  <div style={printStyles.scheduleValue}>{item.value}</div>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        {/* Crew Table */}
        <table style={printStyles.table}>
          <thead>
            <tr>
              <th
                style={{ ...printStyles.tableCell, ...printStyles.tableHeader }}
              >
                Position
              </th>
              <th
                style={{ ...printStyles.tableCell, ...printStyles.tableHeader }}
              >
                Name
              </th>
              <th
                style={{ ...printStyles.tableCell, ...printStyles.tableHeader }}
              >
                Phone
              </th>
              <th
                style={{ ...printStyles.tableCell, ...printStyles.tableHeader }}
              >
                Email
              </th>
            </tr>
          </thead>
          <tbody>
            {Array.isArray(sortedCrewMembers) &&
              sortedCrewMembers.map((member, index) => (
                <tr key={index}>
                  <td style={printStyles.tableCell}>
                    {member.roles?.[0]?.name ||
                      member.assignment?.[0]?.role ||
                      "N/A"}
                  </td>

                  <td style={printStyles.tableCell}>
                    <div>
                      {`${member.crew_member.user.first_name} ${member.crew_member.user.last_name}`}
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginTop: "2px",
                        gap: "4px",
                      }}
                    >
                      {/* Stars */}
                      <span style={{ color: "#FFD700" }}>
                        {Array.from({ length: 5 }, (_, i) => (
                          <span key={i}>
                            {i < (member?.crew_member?.average_rating ?? 0)
                              ? "★"
                              : "☆"}
                          </span>
                        ))}
                      </span>
                    </div>
                  </td>

                  <td style={printStyles.tableCell}>
                    {member.crew_member?.user?.individual?.phone_number ||
                      member.crew_member?.user?.phone_number ||
                      "N/A"}
                  </td>
                  <td style={printStyles.tableCell}>
                    {member.crew_member.user.email || "N/A"}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>

        {/* Client Details */}
        <div style={printStyles.clientDetails}>
          <div style={printStyles.clientBox}>
            <div style={printStyles.clientTitle}>Client Details</div>
            <div style={{ marginBottom: "4px" }}>
              <strong>Name:</strong>{" "}
              {callSheetData?.booking?.contact?.first_name || ""}{" "}
              {callSheetData?.booking?.contact?.last_name || ""}
            </div>
            <div style={{ marginBottom: "4px" }}>
              <strong>Email:</strong>{" "}
              {callSheetData?.booking?.contact?.email || "N/A"}
            </div>
            <div style={{ marginBottom: "4px" }}>
              <strong>Phone:</strong>{" "}
              {callSheetData?.booking?.contact?.phone || "N/A"}
            </div>
          </div>

          <div style={printStyles.clientBox}>
            <div style={printStyles.clientTitle}>Company Details</div>
            <div style={{ marginBottom: "4px" }}>
              <strong>Company:</strong> {companyDetails.name}
            </div>
            <div style={{ marginBottom: "4px" }}>
              <strong>Address:</strong> {companyDetails.address}
            </div>
          </div>
        </div>

        {/* Special Notes */}
        <div style={{ marginTop: "16px" }}>
          <div
            style={{
              fontSize: "12px",
              fontWeight: "bold",
              marginBottom: "8px",
            }}
          >
            Special Notes
          </div>
          <div style={{ fontSize: "10px" }}>{specialRequest}</div>
        </div>
      </div>
    );
  }
);

PrintableCallSheet.displayName = "PrintableCallSheet";

export default PrintableCallSheet;
